import React, { useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';

// Reusing the modal shell styles already built for Verification's edit
// modal, rather than duplicating the same overlay/box/select CSS again.
import {
  ModalOverlay,
  ModalBox,
  ModalTitle,
  ModalSubtitle,
  ModalLabel,
  ModalSelect,
  ModalFooter,
  ModalCancelButton,
  ModalSaveButton,
  StatusMessage,
} from '../../Verification/verification.styles';

import { useZones, usePrecincts, useDevelopments, useConfirmInfo } from '../hook/Useprocessing';

/*
  Props:
   - applicationId (= listId)
   - onClose()
   - onConfirmed() — called after confirmInfo() succeeds
*/

const ConfirmInfoModal = ({ applicationId, onClose, onConfirmed }) => {
  const [zoneId, setZoneId] = useState('');
  const [precinctId, setPrecinctId] = useState('');
  const [developmentId, setDevelopmentId] = useState('');

  const zonesQuery = useZones();
  const precinctsQuery = usePrecincts(zoneId);
  const developmentsQuery = useDevelopments();

  const confirmMutation = useConfirmInfo({
    onSuccess: () => {
      onConfirmed?.();
      onClose?.();
    },
  });

  const handleZoneChange = (e) => {
    setZoneId(e.target.value);
    setPrecinctId(''); // precinct depends on zone — reset when zone changes
  };

  const canSave = zoneId && precinctId && developmentId;

  const handleSave = () => {
    if (!canSave) return;
    confirmMutation.mutate({
      applicationId,
      precinctId,
      zoneId,
      developmentId,
    });
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalTitle>Confirm Application Details</ModalTitle>
        <ModalSubtitle>
          Select the zone, precinct, and development type before continuing.
        </ModalSubtitle>

        <ModalLabel htmlFor="zone-select">Zone</ModalLabel>
        <ModalSelect
          id="zone-select"
          value={zoneId}
          onChange={handleZoneChange}
          disabled={zonesQuery.isLoading || confirmMutation.isPending}
        >
          <option value="" disabled>
            {zonesQuery.isLoading ? 'Loading zones…' : 'Select a zone'}
          </option>
          {(zonesQuery.data?.data ?? []).map((z) => (
            <option key={z._id} value={z._id}>
              {z.name}
            </option>
          ))}
        </ModalSelect>

        <ModalLabel htmlFor="precinct-select">Precinct</ModalLabel>
        <ModalSelect
          id="precinct-select"
          value={precinctId}
          onChange={(e) => setPrecinctId(e.target.value)}
          disabled={!zoneId || precinctsQuery.isLoading || confirmMutation.isPending}
        >
          <option value="" disabled>
            {!zoneId
              ? 'Select a zone first'
              : precinctsQuery.isLoading
                ? 'Loading precincts…'
                : 'Select a precinct'}
          </option>
          {(precinctsQuery.data?.data ?? []).map((p) => (
            <option key={p._id} value={p._id}>
              {p.name}
            </option>
          ))}
        </ModalSelect>

        <ModalLabel htmlFor="development-select">Development</ModalLabel>
        <ModalSelect
          id="development-select"
          value={developmentId}
          onChange={(e) => setDevelopmentId(e.target.value)}
          disabled={developmentsQuery.isLoading || confirmMutation.isPending}
        >
          <option value="" disabled>
            {developmentsQuery.isLoading ? 'Loading developments…' : 'Select a development'}
          </option>
          {(developmentsQuery.data?.data ?? []).map((d) => (
            <option key={d._id} value={d._id}>
              {d.name}
            </option>
          ))}
        </ModalSelect>

        {confirmMutation.isError && (
          <StatusMessage $type="error">
            <AlertCircle size={14} />
            {confirmMutation.error?.message || 'Failed to save. Please try again.'}
          </StatusMessage>
        )}

        <ModalFooter>
          <ModalCancelButton type="button" onClick={onClose} disabled={confirmMutation.isPending}>
            Cancel
          </ModalCancelButton>
          <ModalSaveButton type="button" onClick={handleSave} disabled={!canSave || confirmMutation.isPending}>
            {confirmMutation.isPending ? (
              <>
                <Loader2 size={14} className="spin" /> Saving…
              </>
            ) : (
              'Continue'
            )}
          </ModalSaveButton>
        </ModalFooter>
      </ModalBox>
    </ModalOverlay>
  );
};

export default ConfirmInfoModal;
